// Honeypot fields injected into the surrounding <form>. Three fields with
// different concealment techniques cover different bot heuristics.
//
//   index 0 — display:none (catches bots that read "all visible inputs")
//   index 1 — off-screen (catches bots that filter only display:none)
//   index 2 — sr-only clip (catches bots that filter visibility checks)
//
// The fields intentionally have NO `name` attribute. Inputs without `name` are
// excluded from native form submission and from `new FormData(form)`, so the
// real form payload is never polluted with honeypot values. The widget reads
// the values directly via `inputElement.value` and ships them through the
// captcha channel (POST /v2/verifications, POST .../challenges).
//
// Tarnung: every field gets a randomized id with a plausible-looking prefix
// (email_confirm, website, phone2, …). The DOM is scanned to ensure the id is
// unique within the document. Fields are wrapped in an aria-hidden container.

const ID_PREFIXES = [
  'email_confirm',
  'website',
  'phone2',
  'mobile_alt',
  'fax',
  'address2',
  'company_url',
  'username_alt',
];

type ConcealmentProfile = 'display-none' | 'off-screen' | 'sr-clip';

const PROFILES: ConcealmentProfile[] = ['display-none', 'off-screen', 'sr-clip'];

export type HoneypotFirstInteractionEvent = 'focus' | 'input' | 'change';

export interface HoneypotFieldSnapshot {
  index: number;
  value: string;
  firstInteractionMsRelativeToBoxCreation: number | null;
  firstInteractionEvent: HoneypotFirstInteractionEvent | null;
}

interface HoneypotFieldEntry {
  index: number;
  input: HTMLInputElement;
  wrapper: HTMLElement;
  firstInteractionMsRelativeToBoxCreation: number | null;
  firstInteractionEvent: HoneypotFirstInteractionEvent | null;
  listeners: Array<{ type: string; handler: EventListener }>;
}

export class HoneypotFields {

  private entries: HoneypotFieldEntry[] = [];
  private formElement: HTMLFormElement | null = null;
  private boxCreationTimestamp: number = performance.now();

  public setup(formElement: HTMLFormElement, boxCreationTimestampMs: number): void {

    this.formElement = formElement;
    this.boxCreationTimestamp = boxCreationTimestampMs;

    const usedPrefixes = new Set<string>();

    for (let index = 0; index < PROFILES.length; index++) {
      const profile = PROFILES[index];
      const prefix = pickUnusedPrefix(usedPrefixes);
      const id = generateUniqueId(prefix);

      const { wrapper, input } = createField(profile, id);
      formElement.appendChild(wrapper);

      const entry: HoneypotFieldEntry = {
        index,
        input,
        wrapper,
        firstInteractionMsRelativeToBoxCreation: null,
        firstInteractionEvent: null,
        listeners: [],
      };

      this.attachInteractionListeners(entry);
      this.entries.push(entry);
    }
  }

  public getSnapshot(): HoneypotFieldSnapshot[] {
    return this.entries.map(entry => ({
      index: entry.index,
      value: entry.input.value,
      firstInteractionMsRelativeToBoxCreation: entry.firstInteractionMsRelativeToBoxCreation,
      firstInteractionEvent: entry.firstInteractionEvent,
    }));
  }

  public reset(): void {
    for (const entry of this.entries) {
      entry.input.value = '';
      entry.firstInteractionMsRelativeToBoxCreation = null;
      entry.firstInteractionEvent = null;
    }
  }

  public cleanup(): void {
    for (const entry of this.entries) {
      for (const { type, handler } of entry.listeners) {
        entry.input.removeEventListener(type, handler);
      }
      if (this.formElement && this.formElement.contains(entry.wrapper)) {
        this.formElement.removeChild(entry.wrapper);
      }
    }
    this.entries = [];
  }

  private attachInteractionListeners(entry: HoneypotFieldEntry): void {
    const eventTypes: HoneypotFirstInteractionEvent[] = ['focus', 'input', 'change'];
    for (const type of eventTypes) {
      const handler: EventListener = () => {
        if (entry.firstInteractionMsRelativeToBoxCreation !== null) return;
        entry.firstInteractionMsRelativeToBoxCreation = Math.max(
          0,
          Math.round(performance.now() - this.boxCreationTimestamp),
        );
        entry.firstInteractionEvent = type;
      };
      entry.input.addEventListener(type, handler);
      entry.listeners.push({ type, handler });
    }
  }
}

function pickUnusedPrefix(used: Set<string>): string {
  const candidates = ID_PREFIXES.filter(p => !used.has(p));
  const pool = candidates.length > 0 ? candidates : ID_PREFIXES;
  const choice = pool[Math.floor(Math.random() * pool.length)];
  used.add(choice);
  return choice;
}

function generateUniqueId(prefix: string): string {
  for (let attempt = 0; attempt < 8; attempt++) {
    const id = `${prefix}_${randomSuffix()}`;
    if (!document.getElementById(id)) return id;
  }
  // Extremely unlikely fallback. Still return something unique-ish.
  return `${prefix}_${randomSuffix()}_${Date.now().toString(36)}`;
}

function randomSuffix(): string {
  const cryptoObj = (typeof window !== 'undefined' ? window.crypto : undefined) as Crypto | undefined;
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const bytes = new Uint8Array(6);
    cryptoObj.getRandomValues(bytes);
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
      out += bytes[i].toString(16).padStart(2, '0');
    }
    return out;
  }
  return Math.floor(Math.random() * 0xffffffffff).toString(36);
}

function createField(profile: ConcealmentProfile, id: string): { wrapper: HTMLElement; input: HTMLInputElement } {
  const wrapper = document.createElement('div');
  wrapper.setAttribute('aria-hidden', 'true');
  applyWrapperStyle(wrapper, profile);

  const input = document.createElement('input');
  input.type = 'text';
  input.id = id;
  input.setAttribute('autocomplete', 'off');
  input.setAttribute('tabindex', '-1');
  input.setAttribute('aria-hidden', 'true');
  // Intentionally no `name` attribute: keeps the field out of native
  // form submissions and FormData entries.

  wrapper.appendChild(input);
  return { wrapper, input };
}

function applyWrapperStyle(element: HTMLElement, profile: ConcealmentProfile): void {
  switch (profile) {
    case 'display-none':
      element.style.display = 'none';
      break;
    case 'off-screen':
      element.style.position = 'absolute';
      element.style.left = '-10000px';
      element.style.top = 'auto';
      element.style.width = '1px';
      element.style.height = '1px';
      element.style.overflow = 'hidden';
      element.style.opacity = '0';
      element.style.pointerEvents = 'none';
      break;
    case 'sr-clip':
      // Standard "screen-reader only" clipping pattern. Guaranteed invisible
      // even on very large displays (TVs, projectors) because the visible
      // pixel area is 1×1 and clipped to nothing.
      element.style.position = 'absolute';
      element.style.width = '1px';
      element.style.height = '1px';
      element.style.padding = '0';
      element.style.margin = '-1px';
      element.style.overflow = 'hidden';
      element.style.clip = 'rect(0, 0, 0, 0)';
      (element.style as any).clipPath = 'inset(50%)';
      element.style.whiteSpace = 'nowrap';
      element.style.border = '0';
      element.style.opacity = '0';
      element.style.pointerEvents = 'none';
      break;
  }
}
